import React, { useState, useEffect, useRef, useCallback } from 'react';
import NotificationOverlay from './NotificationOverlay';
import ReactDOM from 'react-dom';
import SnoozeSheet, { snoozeUntil } from './SnoozeSheet';
import { supabase } from '../lib/supabase';
import { useNotificationNav } from '../lib/notificationNav';
import { canSeeNotificationSuggestions, canManageIntegrations, canAccessSpeakUpAdmin, canSeeSpeakUpManager, canSeeSpeakUpLedger } from '../lib/permissions';
import { guideHref, hasGuideSection } from '../lib/guideLinks';
import AuditPill from './AuditPill';
import { isAuditEvent, AUDIT_RAIL, AUDIT_TINT } from '../lib/notificationSeverity';
import { DOMAIN_COLORS, DOMAIN_LABELS, domainOf, chipOf, colorOf, chipsFor } from '../lib/notificationDomain';
import { useAuth } from './AuthContext';
import { PRODUCT_NAME, PRODUCT_UNIT } from '../lib/tenant';
import { canReachPage, pageRuleAuth } from '../lib/deepLink';

// #314 / #315, Session 79.12 — THE THREE MAPS THAT USED TO LIVE HERE ARE GONE.
// They were one of three copies of "which module is this bell from", and the
// other two read the `domain` COLUMN while this one read the event type — so
// this surface painted an invoice green and badged it INVENTORY while the
// mobile sheet beside it painted the same row grey. See notificationDomain.js.


function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

function fmt(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function NotificationCard({ n, onMarkRead, onSnooze, onNavigate, snoozeOpenId, setSnoozeOpenId }) {
  // THE NOTIFICATION, not its event type. A function handed only an event type
  // cannot read the `domain` column, which is why this surface was structurally
  // unable to be right about invoicing. #314.
  const color = colorOf(n);
  const domainKey = domainOf(n);
  const isAudit = isAuditEvent(n.event_type);
  const snoozeBtnRef = useRef(null);

  return (
    <div style={{
      borderBottom: '1px solid #F0F0F0', padding: '12px 14px',
      background: isAudit ? AUDIT_TINT : (n.read ? 'white' : '#FAFFFE'),
      borderLeft: `3px solid ${isAudit ? AUDIT_RAIL : color}`,
      cursor: 'pointer',
      position: 'relative',
    }}
      onClick={() => { onMarkRead(n.id); if (n.link_to) onNavigate(n); }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' }}>
        {!n.read && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: color, flexShrink: 0, marginTop: '5px' }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: n.read ? 500 : 700, color: '#0F0F0F', marginBottom: '2px' }}>{n.title}</div>
          {n.message && <div style={{ fontSize: '12px', color: '#5C5C5C', lineHeight: 1.4 }}>{n.message}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
            <span style={{ fontSize: '11px', color: '#9A9A9A' }}>{fmt(n.created_at)}</span>
            {isAudit && <AuditPill />}
            {domainKey && (
              <span style={{
                fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                padding: '1px 6px', borderRadius: '4px', background: `${color}18`, color,
              }}>{DOMAIN_LABELS[domainKey]}</span>
            )}
          </div>
        </div>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button ref={snoozeBtnRef} onClick={e => { e.stopPropagation(); setSnoozeOpenId(snoozeOpenId === n.id ? null : n.id); }} style={{
            background: 'none', border: '1px solid #E8E8E8', borderRadius: '4px', cursor: 'pointer',
            padding: '3px 7px', fontSize: '11px', color: '#9A9A9A',
          }}>Snooze</button>
          {snoozeOpenId === n.id && (
            <SnoozeSheet anchorRef={snoozeBtnRef} onSnooze={opt => { onSnooze(n.id, opt); setSnoozeOpenId(null); }} onClose={() => setSnoozeOpenId(null)} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── THE DESKTOP BELL'S DROPDOWN — #150, documented at Session 79.12 ──────────
//
// #150 was logged at 62a as "orphaned notification CSS + an UNDOCUMENTED
// NotificationPanel", and this is the documentation half. It had none, which
// is how a component nobody can describe accumulates three private copies of
// a map — the state #314 and #315 were logged for.
//
// WHAT IT IS: the panel behind the bell on DESKTOP. Its mobile counterpart is
// NotificationOverlay.js, a bottom sheet, and the two are separate components
// by design rather than one responsive one — Topbar renders whichever the
// width calls for. They now share their domain logic (notificationDomain.js)
// and their chip row, because keeping two hand-written copies is precisely
// how they came to disagree about which chips exist.
//
// WHAT IT SHOWS: UNREAD only, and unsnoozed — a notification snoozed to
// tomorrow is absent here and still present on the full Notifications page,
// which is the difference between the two surfaces. This is a working tray;
// that is the history.
//
// THE CSS HALF OF #150 was already gone: `.notif-dropdown` was removed at
// Session 68a and only `.notif-panel` survives, which this component uses at
// the div below. Its one rule is a max-width:768px override pinning the panel
// to the viewport edges — that is NOT a locked-rule-3 violation, because this
// is the bell's own dropdown rather than an Overlay portal, and on a phone the
// bell renders NotificationOverlay instead.
function NotificationPanel({ notifications, onMarkRead, onMarkAllRead, onSnooze, onNavigate, isAdmin, profile }) {
  const [activeChip, setActiveChip] = useState('all');
  const [snoozeOpenId, setSnoozeOpenId] = useState(null);

  const unread = notifications.filter(n => !n.read && (!n.snoozed_until || new Date(n.snoozed_until) <= new Date()));
  const canSeeSuggestions = canSeeNotificationSuggestions({ isAdmin, profile });
  const chips = chipsFor({ canSeeSuggestions });
  const chipLabel = { all: 'All', ...DOMAIN_LABELS };

  function chipCount(chip) {
    if (chip === 'all') return unread.length;
    // chipOf, not domainOf: decision 3 folds invoicing under Purchasing, so the
    // Purchasing count includes both ends of the lifecycle.
    return unread.filter(n => chipOf(n) === chip).length;
  }

  const filtered = activeChip === 'all'
    ? unread
    : unread.filter(n => chipOf(n) === activeChip);

  return (
    <div className="notif-panel" style={{
      position: 'absolute', top: 'calc(100% + 8px)', right: '-120px', width: '480px',
      background: 'white', border: '1px solid #E8E8E8', borderRadius: '10px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 1000, overflow: 'visible',
    }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notifications</span>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => onNavigate('notifications')} title="View all" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A9A9A', padding: '2px', display: 'flex' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
          </button>
          <button onClick={() => onNavigate('notification_preferences')} title="Preferences" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A9A9A', padding: '2px', display: 'flex' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 0 0 4.93 19.07M4.93 4.93a10 10 0 0 0 0 14.14"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2"/></svg>
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', padding: '8px 10px', borderBottom: '1px solid #F0F0F0', overflowX: 'auto' }}>
        {chips.map(chip => {
          const count = chipCount(chip);
          const color = chip === 'all' ? '#0F0F0F' : DOMAIN_COLORS[chip];
          const isActive = activeChip === chip;
          return (
            <button key={chip} onClick={() => setActiveChip(chip)} style={{
              flexShrink: 0, padding: '3px 8px', borderRadius: '20px', border: 'none', cursor: 'pointer',
              fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
              background: isActive ? (chip === 'all' ? '#0F0F0F' : color) : '#F0F0F0',
              color: isActive ? 'white' : '#5C5C5C',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}>
              {chipLabel[chip]}
              {count > 0 && <span style={{ background: isActive ? 'rgba(255,255,255,0.3)' : '#D4D4D4', borderRadius: '8px', padding: '0 4px', fontSize: '10px' }}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ overflowY: 'auto', minHeight: '400px', maxHeight: '680px' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>??</div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#9A9A9A' }}>You're all caught up.</div>
          </div>
        ) : filtered.map(n => (
          <NotificationCard
            key={n.id}
            n={n}
            onMarkRead={onMarkRead}
            onSnooze={onSnooze}
            onNavigate={onNavigate}
            snoozeOpenId={snoozeOpenId}
            setSnoozeOpenId={setSnoozeOpenId}
          />
        ))}
      </div>

      <div style={{ padding: '10px 14px', borderTop: '1px solid #F0F0F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {unread.length > 0
          ? <button className="btn btn-ghost btn-sm" style={{ fontSize: '11px' }} onClick={onMarkAllRead}>Mark all read</button>
          : <span />
        }
        <button onClick={() => onNavigate('notifications')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#1D9E75', fontWeight: 600 }}>
          View all notifications ?
        </button>
      </div>
    </div>
  );
}

function NotificationToast({ notification, onDismiss }) {
  const color = colorOf(notification);
  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
      width: '320px', background: 'white', borderRadius: '10px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      borderLeft: `4px solid ${color}`,
      overflow: 'hidden', animation: 'slideInRight 0.25s ease',
    }}>
      <div style={{ padding: '12px 14px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F0F0F', marginBottom: '2px' }}>{notification.title}</div>
          {notification.message && <div style={{ fontSize: '12px', color: '#5C5C5C', lineHeight: 1.4 }}>{notification.message}</div>}
        </div>
        <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A9A9A', fontSize: '16px', lineHeight: 1, padding: '0', flexShrink: 0 }}>×</button>
      </div>
      <div style={{ height: '3px', background: '#F0F0F0', position: 'relative' }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, height: '100%',
          background: color, width: '100%',
          animation: 'toastProgress 4s linear forwards',
        }} />
      </div>
    </div>,
    document.body
  );
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

export default function Topbar({
  user,
  profile,
  active,
  signOut,
  isAdmin,
  isManager,
  isExecutive,
  pendingCount,
  setActive,
  setSelectedItemId,
  setOpenRequestId,
  setOpenTransferId,
  setOpenTransferRequestId,   // #605 - a transfer named by the inbox's bell
  onMenuToggle,
  onLogoClick,
}) {
  // 74z8 — the tenant half of the brand lockup. Read from context rather than
  // added as a prop: App.js already threads eleven props into this component,
  // and the company name is shell-wide state, not something a parent decides.
  const auth = useAuth();
  const { companyName } = auth;
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [toast, setToast] = useState(null);
  const bellRef = useRef(null);
  const navigateFromNotification = useNotificationNav(setActive, setSelectedItemId, setOpenRequestId, setOpenTransferId, setOpenTransferRequestId);

  const unreadCount = notifications.filter(n =>
    !n.read && (!n.snoozed_until || new Date(n.snoozed_until) <= new Date())
  ).length;

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30);
    setNotifications(data || []);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('notifications-topbar')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          setNotifications(prev => [payload.new, ...prev]);
          setToast(payload.new);
          setTimeout(() => setToast(null), 4000);
        }
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    function handleClick(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      setShowUserMenu(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function markRead(id) {
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }

  async function markAllRead() {
    await supabase.from('notifications').update({ read: true }).eq('user_id', user.id).eq('read', false);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }

  async function handleSnooze(id, option) {
    const until = snoozeUntil(option);
    if (!until) return;
    await supabase.from('notifications').update({ snoozed_until: until }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, snoozed_until: until } : n));
  }

  function handleNavigate(target) {
    navigateFromNotification(target);
    setShowNotifications(false);
  }

  return (
    <>
    <header className="topbar">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button className="hamburger" onClick={onMenuToggle} aria-label="Open menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <a className="logo" href="#" onClick={e => { e.preventDefault(); onLogoClick(); }}>
          {/* 74z8, brand identity gate. Two construction rules the gate marks
              as load-bearing, repeated here because this is where a future
              session will try to simplify them away:
              1. The index marks (the two short lines) are what make this a
                 spirit level rather than a crosshair, an aperture or a record
                 button. They are not decoration.
              2. The line must stay BROKEN at the ring. Drawn through, this is
                 the international prohibition sign at 29px. The gap is the
                 whole difference between a level and a "no entry" symbol. */}
          <div className="logo-mark" aria-hidden="true">
            <svg viewBox="0 0 100 100" fill="none">
              <circle cx="50" cy="50" r="34" fill="none" stroke="#39D353" strokeWidth="8" />
              <line x1="5" y1="50" x2="19" y2="50" stroke="#39D353" strokeWidth="8" strokeLinecap="round" />
              <line x1="81" y1="50" x2="95" y2="50" stroke="#39D353" strokeWidth="8" strokeLinecap="round" />
              <circle cx="50" cy="50" r="13" fill="#39D353" />
            </svg>
          </div>
          <div className="logo-wordmark">
            <div className="logo-text">{PRODUCT_NAME}</div>
            {/* Desktop carries Stede's own unit line; the tenant sits across
                the divider. Mobile has no room for both, so the gate drops the
                tenant into this same slot instead — one lockup, two densities. */}
            <span className="logo-sub logo-sub--desktop">{PRODUCT_UNIT}</span>
            <span className="logo-sub logo-sub--mobile">{companyName}{profile?.branch_name ? ` · ${profile.branch_name}` : ''}</span>
          </div>
          <div className="logo-divider" aria-hidden="true" />
          <div className="logo-tenant">
            {companyName}
            {profile?.branch_name && <small>{profile.branch_name}</small>}
          </div>
        </a>
      </div>

      <div className="topbar-right">
        {pendingCount > 0 && (isAdmin || isManager) && (
          <button className="btn btn-warning btn-sm topbar-pending" onClick={() => setActive('requests')}>
            {pendingCount} pending request{pendingCount !== 1 ? 's' : ''}
          </button>
        )}

        <div ref={bellRef} style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ position: 'relative', padding: '6px' }}
            onClick={() => { setShowNotifications(v => !v); if (!showNotifications) loadNotifications(); }}
            aria-label="Notifications"
          >
            <BellIcon />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: '2px', right: '2px',
                background: '#C62828', color: 'white', borderRadius: '50%',
                width: '16px', height: '16px', fontSize: '10px', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          {showNotifications && (
            isMobile ? (
              <NotificationOverlay
                notifications={notifications}
                onMarkRead={markRead}
                onMarkAllRead={markAllRead}
                onSnooze={handleSnooze}
                onNavigate={handleNavigate}
                onClose={() => setShowNotifications(false)}
                isAdmin={isAdmin}
                profile={profile}
              />
            ) : (
              <NotificationPanel
                notifications={notifications}
                onMarkRead={markRead}
                onMarkAllRead={markAllRead}
                onSnooze={handleSnooze}
                onNavigate={handleNavigate}
                isAdmin={isAdmin}
                profile={profile}
              />
            )
          )}
        </div>

        <div style={{ position: 'relative' }}>
          <button
            className="user-chip"
            onClick={() => setShowUserMenu(v => !v)}
            style={{ border: 'none', cursor: 'pointer' }}
          >
            <div className="user-avatar">{getInitials(profile?.full_name)}</div>
            <div>
              <div className="user-name">{profile?.full_name || 'User'}</div>
              <div className="user-role">{profile?.role?.replace('_', ' ') || 'loading'}</div>
            </div>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2.5" style={{ marginLeft: '2px' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {showUserMenu && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              background: 'white', border: '1px solid #E8E8E8',
              borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              minWidth: '220px', zIndex: 300, overflow: 'hidden',
            }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid #F0F0F0' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--black)' }}>{profile?.full_name}</div>
                <div style={{ fontSize: '11px', color: 'var(--green-dk)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {profile?.role?.replace('_', ' ')}
                </div>
              </div>

              <div style={{ padding: '4px 0', borderBottom: '1px solid #F0F0F0' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px' }}
                  onMouseDown={e => { e.stopPropagation(); setShowUserMenu(false); setActive('profile'); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                    <circle cx="12" cy="8" r="4"/>
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                  </svg>
                  My profile
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px' }}
                  onMouseDown={e => { e.stopPropagation(); setShowUserMenu(false); setActive('notification_preferences'); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  Notification preferences
                </button>
              </div>

              {(isAdmin || isManager || isExecutive || canAccessSpeakUpAdmin({ profile })) && (
                <div style={{ padding: '4px 0', borderBottom: '1px solid #F0F0F0' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '0.6px', padding: '6px 14px 2px' }}>
                    {isAdmin ? 'Admin' : isExecutive ? 'Executive' : isManager ? 'Manager' : 'Suggestions'}
                  </div>
                  {/* #811 and #427's one item, Session 296. Offered only where the
                      page rule opens it. The block above shows for executives
                      and Speak Up handlers too, and this item used to go with
                      it — so an executive pressed User management and landed on
                      Reports, and a team member holding Speak Up on Tools. */}
                  {canReachPage(pageRuleAuth(auth), 'users') && (
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px' }}
                    onMouseDown={e => { e.stopPropagation(); setShowUserMenu(false); setActive('users'); }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    User management
                    {(isAdmin || isExecutive) && (
                      <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 500, padding: '2px 7px', borderRadius: '20px', background: '#E1F5EE', color: '#085041' }}>Admin</span>
                    )}
                  </button>
                  )}
                  {(isAdmin || isExecutive) && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px' }}
                      onMouseDown={e => { e.stopPropagation(); setShowUserMenu(false); setActive('company_settings'); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.07 4.93A10 10 0 0 0 4.93 19.07M4.93 4.93a10 10 0 0 0 0 14.14"/>
                        <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
                      </svg>
                      Company settings
                      <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 500, padding: '2px 7px', borderRadius: '20px', background: '#E1F5EE', color: '#085041' }}>Admin</span>
                    </button>
                  )}
                  {/* #298, Session 78n9.3. Its own destination, immediately
                      below Company settings rather than inside it — see
                      IntegrationsPage.js for why, and note this reverses gate
                      decision D14 on Colby's instruction.

                      Gated by the predicate rather than an inline role test,
                      per locked rule 8. The inline (isAdmin || isExecutive)
                      checks around it are pre-existing and are logged as a
                      finding rather than changed here. */}
                  {canManageIntegrations({ isAdmin, isExecutive }) && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px' }}
                      onMouseDown={e => { e.stopPropagation(); setShowUserMenu(false); setActive('integrations'); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                        <rect x="3" y="3" width="7" height="7" rx="1"/>
                        <rect x="14" y="14" width="7" height="7" rx="1"/>
                        <path d="M10 6.5h2.5a1.5 1.5 0 0 1 1.5 1.5v9.5"/>
                      </svg>
                      Integrations
                      <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 500, padding: '2px 7px', borderRadius: '20px', background: '#E1F5EE', color: '#085041' }}>Admin</span>
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px' }}
                      onMouseDown={e => { e.stopPropagation(); setShowUserMenu(false); setActive('printer_registry'); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                        <rect x="6" y="9" width="12" height="7" rx="1"/>
                        <path d="M6 9V4h12v5M6 16v4h12v-4"/>
                      </svg>
                      Label printers
                      <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 500, padding: '2px 7px', borderRadius: '20px', background: '#E1F5EE', color: '#085041' }}>Admin</span>
                    </button>
                  )}
                  {/* #472 / #427's Speak Up quarter, pulled forward from the
                      Oct 21-24 design-system sprint because this is the exact
                      line gate decision D2 narrows. Was the inline expression
                      `(isAdmin || profile?.suggestion_admin)`, a re-implemented
                      copy of isSuggestionAdmin() against locked rule 8 -- which
                      is why it had to be found and changed by hand rather than
                      following the predicate. The other three inline gates in
                      this menu stay at their own sprint. */}
                  {canAccessSpeakUpAdmin({ isAdmin, profile }) && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px' }}
                      onMouseDown={e => { e.stopPropagation(); setShowUserMenu(false); setActive('speak_up_admin'); }}
                    >
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                       <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                     </svg>
                     Speak Up
                     <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 500, padding: '2px 7px', borderRadius: '20px', background: '#F0E8FA', color: '#6B3FA0' }}>Admin</span>
                    </button>
                  )}

                  {/* Speak Up OOB-4. A manager's entry is NOT a cut-down
                      handler view -- it shows their branch's ideas as written
                      plus summaries shared with them, and never a concern.
                      Hidden from whoever already holds the handler entry, so
                      one person never sees two doors to the same module. */}
                  {canSeeSpeakUpManager({ isManager, isExecutive, isAdmin }) && !canAccessSpeakUpAdmin({ isAdmin, profile }) && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px' }}
                      onMouseDown={e => { e.stopPropagation(); setShowUserMenu(false); setActive('speak_up_manager'); }}
                    >
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                       <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                     </svg>
                     Speak Up
                    </button>
                  )}

                  {/* D19's ledger. Counts only -- and the reason it is a
                      separate entry rather than a tab is that an executive
                      who is not the handler has no submission view to tab
                      away from. */}
                  {canSeeSpeakUpLedger({ isExecutive, isAdmin }) && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px' }}
                      onMouseDown={e => { e.stopPropagation(); setShowUserMenu(false); setActive('speak_up_ledger'); }}
                    >
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: 0.5 }}>
                       <path d="M3 3v18h18M9 17V9m4 8V5m4 12v-6"/>
                     </svg>
                     Speak Up oversight
                    </button>
                  )}
                </div>
              )}

              {/* HELP WITH THIS SCREEN — promoted from the footer link row to a
                  real menu item at Colby's instruction, 78o. It shipped as one
                  of three small centred links under Sign out and had two
                  defects there, both of which this promotion fixes at once:

                  1. IT DID NOT OPEN. The document-level mousedown listener in
                     this component calls setShowUserMenu(false) unconditionally
                     — no containment check, unlike the bell above it — so the
                     menu closed and the anchor unmounted before the click could
                     complete. Every BUTTON in this menu carries
                     onMouseDown stopPropagation for exactly that reason; the
                     footer anchors did not, so Privacy and Terms were dead too.
                     All three now stop the event, matching the eight buttons.

                  2. IT WRAPPED ONTO TWO LINES. Three links centred in a 220px
                     menu left this one about 100px, and "Help with this screen"
                     does not fit in 100px at 11px. A full-width row has the
                     whole 220px and reads on one line.

                  The wording still only promises what it can keep: "Help with
                  this screen" when guideLinks.js has a section for the page
                  behind the menu, "User guide" when it does not — see D17. This
                  is the CONTEXTUAL entry point; the sidebar's HELP CENTER row is
                  the browse-and-print one, and they deliberately differ. */}
              <div style={{ padding: '4px 0', borderBottom: '1px solid #F0F0F0' }}>
                <a
                  className="btn btn-ghost btn-sm"
                  href={guideHref(active)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px 9px 40px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px', textDecoration: 'none', color: 'inherit' }}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => setShowUserMenu(false)}
                >
                  {hasGuideSection(active) ? 'Help with this screen' : 'User guide'}
                </a>
              </div>

              <div style={{ padding: '4px 0' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ width: '100%', borderRadius: 0, border: 'none', padding: '9px 14px', justifyContent: 'flex-start', gap: '10px', fontSize: '13px', color: '#C62828' }}
                  onMouseDown={e => { e.stopPropagation(); signOut(); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C62828" strokeWidth="2" style={{ flexShrink: 0 }}>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Sign out
                </button>
              </div>

              {/* #442, Session 78o — the second placement, for people already
                  signed in. Deliberately NOT gated on a role: a privacy policy
                  behind a permission check is the same defect as one nobody can
                  find. Extensionless, because Cloudflare serves /privacy.html
                  as a 308 to /privacy and falls back to the app for unknown
                  paths — a typo here would render a sign-in screen with a 200
                  rather than a 404. Opened in a new tab so reading the policy
                  does not throw away whatever the person was in the middle of. */}
              {/* #442. The two documents nobody opens on purpose, kept small and
                  last. Help used to sit here with them and no longer does — it is
                  a menu row above, because it is the one of the three somebody
                  actually goes looking for.

                  Both carry .html so they resolve on the dev server as well as on
                  production, where Cloudflare 308-redirects to the clean URL.
                  Shipped extensionless first and dead on localhost — see the note
                  in Login.js. And both stop mousedown, or this menu unmounts them
                  before the click lands; that was live for the whole of 78o and is
                  why neither link opened. */}
              <div style={{ padding: '8px 14px', borderTop: '1px solid #EFEFEF', fontSize: '11px', color: '#9A9A9A', textAlign: 'center' }}>
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer" onMouseDown={e => e.stopPropagation()} style={{ color: '#7A7A7A', textDecoration: 'none' }}>Privacy</a>
                <span style={{ margin: '0 8px', color: '#D8D8D8' }}>·</span>
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" onMouseDown={e => e.stopPropagation()} style={{ color: '#7A7A7A', textDecoration: 'none' }}>Terms</a>
              </div>
            </div>
          )}
        </div>

        <button className="btn btn-ghost btn-sm topbar-signout" onClick={signOut}>Sign out</button>
      </div>
    </header>
    {toast && <NotificationToast notification={toast} onDismiss={() => setToast(null)} />}
    </>
  );
}
